import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronRightIcon, DownloadIcon, GridIcon } from '@treams/treams-icons';
import { Accordion } from '@treams/treams-ui/common/Accordion';
import { Avatar } from '@treams/treams-ui/common/Avatar';
import { Card } from '@treams/treams-ui/common/Card';
import { Delta } from '@treams/treams-ui/common/Delta';
import { Empty } from '@treams/treams-ui/common/Empty';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { SegmentedControl } from '@treams/treams-ui/common/SegmentedControl';
import { Skeleton } from '@treams/treams-ui/common/Skeleton';
import { Heading3, Label, Paragraph } from '@treams/treams-ui/common/Text';
import { TalentReviewGrid } from '@treams/treams-ui/common/analytics/TalentReviewGrid';
import { IconButton } from '@treams/treams-ui/common/button/IconButton';
import { Checkbox } from '@treams/treams-ui/form/controls/Checkbox';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { exportTalentReviewToExcel } from './talentReviewExcelExport';
import {
	type TalentReviewAnalyticsCell,
	type TalentReviewCellEmployee,
} from './useTalentReviewAnalytics';

export type TalentReviewCardView = 'grid' | 'list';

const LegendSwatch = styled(Box)<{ $kind: 'onNorm' | 'offNorm' | 'farOff' | 'norm' }>`
	width: 10px;
	height: 10px;
	border-radius: 50%;
	flex-shrink: 0;
	background-color: ${(props) =>
		props.$kind === 'offNorm'
			? props.theme.colors.warning
			: props.$kind === 'farOff'
				? props.theme.colors.red
				: tokens.surface.secondary};
	border: ${(props) =>
		props.$kind === 'norm'
			? `1px dashed ${tokens.text.secondary}`
			: props.$kind === 'onNorm'
				? `1px solid ${tokens.border.subtle}`
				: 'none'};
`;

function NormLegend() {
	const { t } = useTranslation();

	const items: Array<{ kind: 'onNorm' | 'offNorm' | 'farOff' | 'norm'; label: string }> = [
		{ kind: 'onNorm', label: t('analytics.talentReview.legend.onNorm') },
		{ kind: 'offNorm', label: t('analytics.talentReview.legend.offNorm') },
		{ kind: 'farOff', label: t('analytics.talentReview.legend.farOffNorm') },
		{ kind: 'norm', label: t('analytics.talentReview.legend.norm') },
	];

	return (
		<Flex flexWrap="wrap" alignItems="center">
			{items.map((item) => (
				<Flex key={item.kind} alignItems="center" mr={3} mb={1}>
					<LegendSwatch $kind={item.kind} mr={1} />
					<Paragraph small color="slateBlue">
						{item.label}
					</Paragraph>
				</Flex>
			))}
		</Flex>
	);
}

function CellEmployees({
	employees,
	showDepartment,
}: {
	employees: TalentReviewCellEmployee[];
	showDepartment: boolean;
}) {
	const { t } = useTranslation();

	if (employees.length === 0) {
		return <Paragraph small>{t('analytics.talentReview.list.empty')}</Paragraph>;
	}

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.list}>
			{employees.map((employee) => (
				<FlexGap key={employee.id} alignItems="center" gap={tokens.gap.list}>
					<Avatar user={employee} size={32} flexShrink={0} />
					<FlexGap alignItems="baseline" gap={tokens.gap.inline}>
						<Label>{`${employee.firstName} ${employee.lastName}`.trim()}</Label>
						{showDepartment && employee.departmentName && (
							<Paragraph small>{`• ${employee.departmentName}`}</Paragraph>
						)}
					</FlexGap>
				</FlexGap>
			))}
		</FlexGap>
	);
}

/**
 * Remount the list when who sits in each cell changes, so open/closed starts over.
 * @param rows Cells on the grid, keyed by the people placed in them.
 */
function cellListKey(rows: TalentReviewAnalyticsCell[][]) {
	return rows
		.map((row) =>
			row.map((cell) => cell.employees.map((employee) => employee.id).join(',')).join('|'),
		)
		.join(';');
}

function TalentReviewCellList({
	rows,
	xAxisLabel,
	yAxisLabel,
	showDepartment,
}: {
	rows: TalentReviewAnalyticsCell[][];
	xAxisLabel: string;
	yAxisLabel: string;
	showDepartment: boolean;
}) {
	return (
		<TalentReviewCellListBody
			key={cellListKey(rows)}
			rows={rows}
			xAxisLabel={xAxisLabel}
			yAxisLabel={yAxisLabel}
			showDepartment={showDepartment}
		/>
	);
}

function TalentReviewCellListBody({
	rows,
	xAxisLabel,
	yAxisLabel,
	showDepartment,
}: {
	rows: TalentReviewAnalyticsCell[][];
	xAxisLabel: string;
	yAxisLabel: string;
	showDepartment: boolean;
}) {
	const axes = [yAxisLabel, xAxisLabel].filter(Boolean).join(' • ');
	const [open, setOpen] = useState<Record<string, boolean>>(() => {
		const initial: Record<string, boolean> = {};

		rows.forEach((row, rowIndex) => {
			row.forEach((cell, columnIndex) => {
				initial[`${rowIndex}-${columnIndex}`] = cell.employees.length > 0;
			});
		});

		return initial;
	});

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.list}>
			{axes && <Paragraph small>{axes}</Paragraph>}
			<Box>
				{rows.flatMap((row, rowIndex) =>
					row.map((cell, columnIndex) => {
						const id = `${rowIndex}-${columnIndex}`;
						const expanded = open[id] ?? cell.employees.length > 0;

						return (
							<Accordion
								key={id}
								borderless
								noPadding
								initialIsCollapsed={!expanded}
								isExpanded={expanded}
								onClick={() => setOpen((current) => ({ ...current, [id]: !expanded }))}
								customHeader={
									<FlexGap
										alignItems="center"
										gap={tokens.gap.list}
										style={{
											paddingTop: tokens.padding.control,
											paddingBottom: tokens.padding.control,
										}}
									>
										<Box
											style={{
												display: 'flex',
												flexShrink: 0,
												color: tokens.icon.tertiary,
												transform: expanded ? 'rotate(90deg)' : undefined,
												transition: 'transform 120ms ease-out',
											}}
										>
											<ChevronRightIcon size={16} />
										</Box>
										<Box flex={1} minWidth={0}>
											<Heading3>{cell.title}</Heading3>
										</Box>
										<FlexGap alignItems="center" gap={tokens.gap.list} flexShrink={0}>
											<Paragraph small>{`${Math.round(cell.value)}%`}</Paragraph>
											{cell.previousValue !== undefined && (
												<Delta
													unit="percentagePoint"
													currentValue={cell.value}
													previousValue={cell.previousValue}
													tooltip={cell.deltaTooltip}
												/>
											)}
										</FlexGap>
									</FlexGap>
								}
							>
								<Box
									style={{
										paddingLeft: tokens.gap.section + tokens.gap.list,
										paddingBottom: tokens.padding.container,
									}}
								>
									<CellEmployees employees={cell.employees} showDepartment={showDepartment} />
								</Box>
							</Accordion>
						);
					}),
				)}
			</Box>
		</FlexGap>
	);
}

export function TalentReviewGridCard({
	rows,
	xAxisLabel,
	yAxisLabel,
	placedCount,
	allDepartments,
	hasNorm,
	showNorm,
	onToggleNorm,
	view = 'grid',
	onViewChange,
	gridName = '',
	empty,
}: {
	rows: TalentReviewAnalyticsCell[][];
	xAxisLabel: string;
	yAxisLabel: string;
	placedCount: number;
	allDepartments: boolean;
	hasNorm: boolean;
	showNorm: boolean;
	onToggleNorm: () => void;
	view?: TalentReviewCardView;
	onViewChange?: (view: TalentReviewCardView) => void;
	gridName?: string;
	empty?: ReactNode;
}) {
	const { t } = useTranslation();
	const showDepartment = useMemo(() => {
		const names = new Set(
			rows.flatMap((row) =>
				row.flatMap((cell) =>
					cell.employees.map((employee) => employee.departmentName).filter(Boolean),
				),
			),
		);

		return names.size > 1;
	}, [rows]);

	const onDownload = async () => {
		try {
			await exportTalentReviewToExcel({
				rows,
				gridName,
				labels: {
					cell: t('analytics.talentReview.export.gridPosition'),
					employee: t('analytics.talentReview.succession.column.employee'),
					jobTitle: t('common.jobTitle'),
					department: t('analytics.talentReview.departments.column.department'),
					manager: t('analytics.talentReview.departments.column.manager'),
					xAxis: xAxisLabel,
					yAxis: yAxisLabel,
					lastPlaced: t('analytics.talentReview.retentionRisk.column.updatedAt'),
				},
			});
		} catch {
			alert(t('analytics.export.error'));
		}
	};

	return (
		<Card style={{ overflow: 'visible' }} height="100%">
			<Flex alignItems="flex-start" justifyContent="space-between" flexWrap="wrap">
				<Box>
					<Heading3 mb={2}>{t('analytics.talentReview.card.title')}</Heading3>
					<Paragraph color="slateBlue">
						{allDepartments
							? t('analytics.talentReview.card.subtitleAll', { count: placedCount })
							: t('analytics.talentReview.card.subtitle', { count: placedCount })}
					</Paragraph>
				</Box>
				{(placedCount > 0 || (view === 'grid' && hasNorm)) && (
					<FlexGap alignItems="center" gap={tokens.gap.list} flexShrink={0}>
						{placedCount > 0 && (
							<SegmentedControl
								value={view}
								options={[
									{ title: t('analytics.talentReview.view.grid'), value: 'grid' },
									{ title: t('analytics.talentReview.view.list'), value: 'list' },
								]}
								onChange={(value) => {
									if (value === 'grid' || value === 'list') {
										onViewChange?.(value);
									}
								}}
							/>
						)}
						{view === 'grid' && hasNorm && (
							<Checkbox
								checked={showNorm}
								label={t('analytics.talentReview.card.showNorm')}
								onClick={onToggleNorm}
							/>
						)}
						{placedCount > 0 && (
							<IconButton
								type="secondary"
								icon={<DownloadIcon />}
								title={t('analytics.metric.downloadButton')}
								aria-label={t('analytics.metric.downloadButton')}
								onClick={onDownload}
							/>
						)}
					</FlexGap>
				)}
			</Flex>
			{view === 'grid' && showNorm && (
				<Box mt={3}>
					<NormLegend />
				</Box>
			)}
			<Box mt={4}>
				{placedCount === 0 ? (
					(empty ?? (
						<Skeleton.Empty
							contents={
								<Empty
									small
									icon={<GridIcon />}
									title={t('analytics.talentReview.empty.noPlacements.title')}
									description={t('analytics.talentReview.empty.noPlacements.description')}
								/>
							}
						>
							<TalentReviewGrid.Skeleton />
						</Skeleton.Empty>
					))
				) : view === 'list' ? (
					<TalentReviewCellList
						rows={rows}
						xAxisLabel={xAxisLabel}
						yAxisLabel={yAxisLabel}
						showDepartment={showDepartment}
					/>
				) : (
					<TalentReviewGrid
						rows={rows}
						variant={showNorm ? 'norm' : 'delta'}
						xAxisLabel={xAxisLabel}
						yAxisLabel={yAxisLabel}
					/>
				)}
			</Box>
		</Card>
	);
}

TalentReviewGridCard.Skeleton = function TalentReviewGridCardSkeleton() {
	return (
		<Card style={{ overflow: 'visible' }} height="100%">
			<Flex alignItems="flex-start" justifyContent="space-between" flexWrap="wrap">
				<Box>
					<Heading3.Skeleton width={160} />
					<Paragraph.Skeleton lines={1} mt={2} width={280} />
				</Box>
				<Flex mt={1} alignItems="center">
					<Skeleton.Box width={16} height={16} mr={2} />
					<Paragraph.Skeleton lines={1} width={140} />
				</Flex>
			</Flex>
			<Box mt={4}>
				<TalentReviewGrid.Skeleton />
			</Box>
		</Card>
	);
};
